import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  VStack,
  HStack,
  Badge,
  Box,
  Divider,
} from '@chakra-ui/react';
import { LeagueId, Match } from '../../types';
import {
  PointBreakdownItem,
  getPointReasonLabel,
} from '../../services/scoringService';
import { MatchScoreHeader } from '../ui/MatchScoreHeader';
import { ResponsiveTableContainer } from '../ui/ResponsiveTableContainer';
import { useAppColors } from '../../hooks/useAppColors';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import { getJornadaLabel, getMundialGroupMatchday, getMundialGroupMatches, parseLigaMxJornadaNumber } from '../../services/matchdayService';

interface ParticipantPointsModalProps {
  isOpen: boolean;
  onClose: () => void;
  participantName: string;
  totalPoints: number;
  stageTitle?: string;
  items: PointBreakdownItem[];
  league: LeagueId;
  teamLogos?: Record<string, string>;
}

function getPhaseBadgeLabel(phase: string | undefined, league: LeagueId): string | null {
  if (!phase) return null;
  if (league === 'mundial_fifa' && phase.startsWith('Grupo')) return phase;
  const jornadaNum = parseLigaMxJornadaNumber(phase);
  if (jornadaNum) return `Jornada ${jornadaNum}`;
  return phase;
}

function getMundialMatchLabel(item: PointBreakdownItem): string | null {
  if (!item.match) return null;
  const groupPool = getMundialGroupMatches([item.match as Match]);
  if (groupPool.length === 0) return null;
  const jornada = getMundialGroupMatchday(item.match as Match, groupPool);
  return getJornadaLabel('mundial_fifa', jornada);
}

export const ParticipantPointsModal: React.FC<ParticipantPointsModalProps> = ({
  isOpen,
  onClose,
  participantName,
  totalPoints,
  stageTitle,
  items,
  league,
  teamLogos,
}) => {
  const { subtle } = useAppColors();
  const breakdownTotal = items.reduce((sum, item) => sum + item.points, 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent mx={4}>
        <ModalHeader pb={2}>
          Puntos de {participantName}
          {stageTitle && (
            <Text fontSize="sm" fontWeight="normal" color={subtle} mt={1}>
              {stageTitle}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <HStack justify="space-between" mb={4} flexWrap="wrap" gap={2}>
            <Text fontSize="sm" color={subtle}>
              Partidos y bonos donde obtuvo puntos
            </Text>
            <Badge colorScheme="gold" fontSize="sm" px={2} py={1}>
              {breakdownTotal} / {totalPoints} pts
            </Badge>
          </HStack>

          {items.length === 0 ? (
            <Text color={subtle} fontSize="sm" textAlign="center" py={8}>
              Este participante aún no ha obtenido puntos en partidos con resultado.
            </Text>
          ) : (
            <VStack spacing={4} align="stretch">
              {items.map((item) => {
                const phaseLabel =
                  league === 'mundial_fifa' && item.match
                    ? getMundialMatchLabel(item)
                    : getPhaseBadgeLabel(item.phase, league);

                if (item.reason === 'stage_goals_bonus') {
                  return (
                    <Box
                      key={item.id}
                      p={4}
                      borderWidth="1px"
                      borderRadius="lg"
                      borderColor="gold.400"
                      bg="gold.50"
                      _dark={{ bg: 'rgba(229, 160, 32, 0.08)' }}
                    >
                      <HStack justify="space-between" mb={2} flexWrap="wrap" gap={2}>
                        <Text fontWeight="bold" fontSize="sm">
                          {item.label}
                        </Text>
                        <Badge colorScheme="gold">+{item.points} pts</Badge>
                      </HStack>
                      <Text fontSize="sm" color={subtle}>
                        Total de goles pronosticados: {item.predictedScoreA} · Total real:{' '}
                        {item.actualScoreA}
                      </Text>
                      <Text fontSize="xs" color={subtle} mt={1}>
                        {getPointReasonLabel(item.reason)}
                      </Text>
                    </Box>
                  );
                }

                const match = item.match!;

                const teamB = match.team_b ?? '';

                return (
                  <Box
                    key={item.id}
                    p={4}
                    borderWidth="1px"
                    borderRadius="lg"
                  >
                    <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
                      {phaseLabel && (
                        <Badge variant="outline" colorScheme="pitch" fontSize="xs">
                          {phaseLabel}
                        </Badge>
                      )}
                      <Badge colorScheme="gold">+{item.points} pts</Badge>
                    </HStack>

                    <MatchScoreHeader
                      teamA={match.team_a}
                      teamB={teamB}
                      scoreA={item.actualScoreA}
                      scoreB={item.actualScoreB}
                      teamLogos={{
                        ...teamLogos,
                        [match.team_a]: teamLogos?.[match.team_a] || match.team_a_logo || '',
                        ...(teamB
                          ? { [teamB]: teamLogos?.[teamB] || match.team_b_logo || '' }
                          : {}),
                      }}
                      logoSize="24px"
                    />

                    <ResponsiveTableContainer mt={3}>
                      <Table size="sm" variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Pronóstico</Th>
                            <Th>Resultado</Th>
                            <Th>Motivo</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          <Tr>
                            <Td fontWeight="semibold">
                              {item.predictedScoreA} – {item.predictedScoreB}
                            </Td>
                            <Td fontWeight="semibold">
                              {item.actualScoreA} – {item.actualScoreB}
                            </Td>
                            <Td fontSize="sm" color={subtle}>
                              {getPointReasonLabel(item.reason)}
                            </Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </ResponsiveTableContainer>
                  </Box>
                );
              })}

              <Divider />

              <HStack justify="space-between">
                <Text fontWeight="bold">Total en partidos listados</Text>
                <Text fontWeight="bold" fontSize="lg">
                  {breakdownTotal} pts
                </Text>
              </HStack>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
